import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { removerCaracteresEspeciais } from '../../../util/formatters/stringNormalizer';
import { CollapsibleOrderableList } from '../../../components';
import { fetchAnswers } from '../../../stores/actions/alternatives';
import { isNullOrEmptyArray } from '../../../util/arrays';

/**
 * Faz o pareamento das alternativas (alts) com o dataToMatch
 * por titulo(label)
 */
function autoMatch(dataToMatch, data) {
  const matchs = [];

  // salva o índice todos os itens que não deram match com nenhum item de dataToMatch
  const notMatchedItemsIdxs = [];
  // salva todas os índices de dataToMatch que não foram alvo de nenhum match de data
  const availableDataToMatchItemsIdx = new Set(dataToMatch.map((_, idx) => idx));
  // temporário
  const temp = [...data];
  temp.forEach((item, itemIdx) => {
    const matchedIdx = dataToMatch
      .findIndex((current) => item.value === current.value);

    if (matchedIdx < 0) {
      notMatchedItemsIdxs.push(itemIdx);
      return;
    }

    availableDataToMatchItemsIdx.delete(matchedIdx);

    temp[itemIdx] = {
      ...item,
      order: matchedIdx + 1,
    };

    matchs.push({
      source: dataToMatch[matchedIdx].value,
      matched: item.value,
    });
  });

  return matchs;
}

function SingleQuestionAwareCollapsibleOrderableList({
  /**
   * identificador do cliente selecionado atualmente
   */
  customerId,
  /**
   * identificador da pesquisa selecionada atualmente
   */
  surveyId,
  /**
   * identificador da pergunta
   */
  questionId,
  /**
   * enunciado da pergunta
   */
  questionTitle,
  /**
   * define se o título poderá ou não ser modificado (checked or unchecked)
   */
  readOnlyTitle = false,
  /**
   * define se este componente foi selecionado
   */
  checkedTitle = false,
  /**
   * define se o título deve ser destacado
   */
  boldedTitle = false,
  /**
   * define se os itens serão reordenados (numericamente)
   */
  reorderable = false,
  /**
   * define os dados de correspodência (da pergunta principal)
   */
  dataToMatch = [/* {label, value, order} */],
  /**
   * define as combinações iniciais das alternativas em relação ao dataToMatch
   */
  matchedAlternatives = [/* {source, matched} */],
  /**
   * propaga o evento de alteração da ordem de combinação entre data e dataToMatch
   */
  onChangeMatchs,
  /**
   * flag inficando se o auto match deve ser realizado automaticamente
   */
  enableAutoMatch = true,
  /**
   * propaga o evento de match automático
   * params: matchs = [{source, matched}]
   */
  onAutoMatch,
  /**
   * outras propriedades
   */
  ...props
}) {
  // from redux
  const { dispatch, running, error, alternatives } = props;

  //
  const [internalCheckedTitle, setInternalCheckedTitle] = useState(checkedTitle);
  const [internalData, setInternalData] = useState([/* { value, label, order } */]);
  // Estado relativo aos dados de match Automatico
  const [autoMatchData, setAutomatchData] = useState({/* { surveyId, questionId, match } */ });

  /**
   * Efeito de ocorrencia de um erro que será mapeado na variável "error".
   */
  useEffect(() => {
    if (error) {
      toast.error(error.toString());
    }
  }, [error]);

  /**
   * Efeito que monitora alterações no estado responsável pelos dados de match automático(autoMatchData)
   * propagando o evento onAutoMatch
   */
  useEffect(() => {
    if (onAutoMatch && !isNullOrEmptyArray(autoMatchData.matchs)) onAutoMatch(autoMatchData);
  }, [autoMatchData, onAutoMatch]);

  /**
   * Efeito da alteração das alternativas, mas só condicionado a
   * "data" não ter dados ainda.
   * Esse efeito é resultado da manipulação do evento de expansão do componente
   * CollapsibleOrderableList, que executa "fetchAnswers"
   */
  useEffect(() => {
    // Verificamos se possui matches entre as alternativas
    const withMatches = !!matchedAlternatives.filter((match) => !!match.source).length;
    const internalDataIsEmpty = isNullOrEmptyArray(internalData);
    // Verificamos se os dados internos (são os dados referentes às alternativas)
    // não possui ordem definida em suas alternativas
    const internalDataWithNoOrder = !internalDataIsEmpty && (
      internalData.filter((alt) => alt.order === null).length === internalData.length
    );

    // Adicionamos uma verificação extra para resolver o problema de sincronismo:
    // se as alternativas possuem matches AND os dados internos não possuem ordem definida
    // isso significa que eles não estão no mesmo momento de renderização, e o estado
    // de internalData precisa ser setado novamente para ser atualizado.
    if ((internalDataIsEmpty || (withMatches && internalDataWithNoOrder)) && !isNullOrEmptyArray(alternatives)) {
      /**
       * somente as alternativas fixas recebem uma ordem, caso as alternativas sejam passiveis
       * de re-ordenacao, elas terão suas ordens definidas no ato ou via match automatico
       * das alternativas
       */
      const alts = alternatives.map((alt, idx) => ({ value: alt.id, label: alt.enunciado, order: reorderable ? null : idx + 1 }));
      const tmps = [...alts];
      let matchs = [];

      if (!isNullOrEmptyArray(dataToMatch)) {
        matchedAlternatives.forEach((match) => {
          alts.forEach((currentAlternative, x) => {
            if ((currentAlternative.value === match.matched) && match.source) {
              // find order (source)
              const sIndex = dataToMatch.findIndex((d) => d.value === match.source);
              const sOrder = dataToMatch[sIndex].order;
              // find order (match)
              const mIndex = x;
              const mOrder = currentAlternative.order;
              // change
              const tIndex = tmps.findIndex((d) => d.order === sOrder);
              if (tIndex > -1) {
                alts[tIndex].order = mOrder;
              }
              alts[mIndex].order = sOrder;
            }
          });
        });

        if (enableAutoMatch) {
          matchs = autoMatch(dataToMatch, alts);
        }
      }
      setInternalData(alts);
      /**
       * Caso o array de match automatico(matchs) seja preenchido
       * os dados do match automatico serão armazenados no estado
       * responsável pelos dados de automatch
       */
      if (!isNullOrEmptyArray(matchs) && enableAutoMatch) {
        setAutomatchData({ surveyId, questionId, matchs });
      }

      if (!internalCheckedTitle) {
        setInternalCheckedTitle(true);
      }
    }
  }, [alternatives, internalData, internalCheckedTitle, matchedAlternatives, dataToMatch, questionId, surveyId]);

  // manipula o evento de expansão do componente CollapsibleOrderableList
  const handleExpandCallback = React.useCallback(() => {
    setInternalData((_internalData) => {
      if (isNullOrEmptyArray(_internalData)) {
        fetchAnswers({ customerId, surveyId, questionId }, dispatch);
      }
      return _internalData;
    });
  }, [customerId, surveyId, questionId]);

  /**
   * @param { source, matched } matches
   */
  const handleChangeMatchesCallback = React.useCallback((matches) => {
    if (onChangeMatchs) onChangeMatchs(surveyId, questionId, matches);
  }, [surveyId, questionId, onChangeMatchs]);

  return (
    <CollapsibleOrderableList
      reorderable={reorderable}
      checkedTitle={internalCheckedTitle}
      readOnlyTitle={readOnlyTitle}
      boldedTitle={boldedTitle}
      title={questionTitle}
      loading={running}
      data={internalData}
      dataToMatch={dataToMatch}
      onExpand={handleExpandCallback}
      onChangeMatchs={handleChangeMatchesCallback}
    />
  );
}

/**
 * Mapeia o estado da store para as propriedades que serão passadas para este
 * componente.
 *
 * O estado é "alternatives" que é um mapa de dados cuja chave é o questionId.
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state, props) => {
  // props
  const { customerId, surveyId, questionId } = props;
  // state key
  const key = { customerId, surveyId, questionId };
  // get alternative state by key
  const alternativesState = state.alternatives;
  const currState = alternativesState.get(key);
  // if exists
  return {
    running: currState ? currState.running : false,
    error: currState ? currState.error : null,
    alternatives: currState ? currState.payload : [],
  };
};


// create component (delegate) and export
export default connect(
  mapStateToProps, null, null, { forwardRef: false },
)(
  React.memo(SingleQuestionAwareCollapsibleOrderableList),
);
